import {useCallback,useEffect,useMemo,useState} from 'react';
import {loadLegacyStats} from './api';
import {loadIdentityStatus,loadParticipant,loadSafetyStatus,loadSpaces} from './apiCoreProxy';
import {hasOwnerPilotAccess} from './contracts';
import {hasLocalVault} from './legacyCrypto';
import type {IdentityStatus,LegacyStats,Participant,SafetyStatus,Space} from './types';

const EMPTY:LegacyStats={moments:0,treasures:0,images:0};

export function useCommunitySession(sessionVersion=0){
 const[participant,setParticipant]=useState<Participant|null>(null);
 const[spaces,setSpaces]=useState<Space[]>([]);
 const[stats,setStats]=useState<LegacyStats>(EMPTY);
 const[safety,setSafety]=useState<SafetyStatus|null>(null);
 const[identity,setIdentity]=useState<IdentityStatus|null>(null);
 const[localVault,setLocalVault]=useState<boolean|null>(null);
 const[error,setError]=useState('');
 const[loading,setLoading]=useState(true);

 const refreshSpaces=useCallback(async()=>{
  setError('');
  try{setSpaces(await loadSpaces());}
  catch(reason){setError(reason instanceof Error?reason.message:'Die Räume konnten nicht neu geladen werden.');}
 },[]);

 useEffect(()=>{
  let active=true;
  setLoading(true);
  setError('');
  setParticipant(null);
  setSpaces([]);
  setStats(EMPTY);
  setSafety(null);
  setIdentity(null);
  setLocalVault(null);

  async function load(){
   try{
    const person=await loadParticipant();
    if(!active)return;
    setParticipant(person);

    const roomList=await loadSpaces();
    if(!active)return;
    setSpaces(roomList);

    const results=await Promise.allSettled([
     loadLegacyStats(),loadSafetyStatus(),loadIdentityStatus(),
    ]);
    if(!active)return;
    if(results[0].status==='fulfilled')setStats(results[0].value);
    if(results[2].status==='fulfilled')setIdentity(results[2].value);
    if(results[1].status==='fulfilled'){
     setSafety(results[1].value);
     setLocalVault(await hasLocalVault(results[1].value.participant_id));
    }
   }catch(reason){
    if(active)setError(reason instanceof Error?reason.message:'Die Community konnte nicht geöffnet werden.');
   }finally{
    if(active)setLoading(false);
   }
  }
  void load();
  return()=>{active=false;};
 },[sessionVersion]);

 const accessible=useMemo(()=>spaces.filter(space=>space.status==='active'),[spaces]);
 const owner=participant?hasOwnerPilotAccess(participant,identity,spaces):false;
 return{participant,spaces,accessible,stats,safety,identity,localVault,error,loading,owner,refreshSpaces};
}
